import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import { ApprovalSetupUrgentUpdateInput } from 'common/types/globalTypes';
import React, { useState } from 'react';

import {
  ApprovalSetupUrgentUpdate,
  ApprovalSetupUrgentUpdateVariables,
} from './__generated__/ApprovalSetupUrgentUpdate';
import { useStyles } from './index.styles';
import { Approval } from '../__generated__/Approval';

import APPROVAL_SETUP_URGENCY_UPDATE from './ApprovalSetupUrgentUpdate.graphql';
interface UrgencyFolderProps {
  approvalDetailsData?: Approval;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  approvalDetailsData,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(approvalDetailsData?.approval?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [approvalSetupUrgencyUpdate] = useMutation<
    ApprovalSetupUrgentUpdate,
    ApprovalSetupUrgentUpdateVariables
  >(APPROVAL_SETUP_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      approvalDetailsData?.approval?._urgencyLevel === 0 ? 1 : 0;

    const { errors } = await approvalSetupUrgencyUpdate({
      variables: {
        input: {
          entityId: approvalDetailsData?.approval?.id!,
          rowTimestamp: approvalDetailsData?.approval?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as ApprovalSetupUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!approvalDetailsData?.approval?.id) {
          cache.modify({
            id: cache.identify({ ...approvalDetailsData.approval }),
            fields: {
              _urgencyLevel: () => {
                return data?.approvalSetupUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length)
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Urgency status updated successfully.',
        appearance: 'success',
      });
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
